/**
 * /market/:slug/marca/:marca — Landing comercial de marca (Ola 1.2).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { Award } from "lucide-react";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketLandingPage } from "@/components/market/MarketLandingPage";
import {
  findBrandByParam,
  getPublishedPlp,
  landingPath,
  landingSeoHead,
  resolveLandingConfig,
} from "@/lib/market-landing";
import { slugifyName } from "@/lib/slug";
import { MarketPlpSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/marca/$marca")({
  pendingComponent: MarketPlpSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: BrandLandingRoute,
  head: ({ params, loaderData }) => {
    const manifestLink = { rel: "manifest", href: `/api/manifest/${params.slug}` };
    const result = loaderData as MarketCatalogResult | undefined;
    if (!result?.ok) {
      return { meta: [{ title: `${params.marca} — ${params.slug}` }], links: [manifestLink, FONT_LINK] };
    }

    const data = result.data;
    const brandName = findBrandByParam(data.products, params.marca);
    if (!brandName) {
      return {
        meta: [{ title: `Marca — ${data.tenant.name}` }],
        links: [manifestLink, FONT_LINK],
      };
    }

    const plp = getPublishedPlp(data);
    const landing = plp ? resolveLandingConfig(plp, "brand", brandName) : null;
    const seo = landing
      ? landingSeoHead({
          storeName: data.tenant.name,
          landing,
          fallbackTitle: brandName,
          canonicalPath: landingPath(params.slug, "brand", slugifyName(brandName)),
        })
      : null;

    return {
      meta: seo?.meta ?? [
        { title: `${brandName} — ${data.tenant.name}` },
        { name: "description", content: `Productos ${brandName} en ${data.tenant.name}` },
      ],
      links: [...(seo?.links ?? []), manifestLink, FONT_LINK],
      scripts: seo?.jsonLd
        ? [
            {
              tag: "script" as const,
              attrs: { type: "application/ld+json" },
              children: JSON.stringify(seo.jsonLd),
            },
          ]
        : undefined,
    };
  },
});

function BrandLandingRoute() {
  const { slug, marca } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!result.ok) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <Award className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
        <p className="text-muted-foreground">{result.error}</p>
      </div>
    );
  }

  const data = result.data;
  const brandName = findBrandByParam(data.products, marca);

  if (!brandName) {
    return (
      <div className="market-surface min-h-screen" style={data.brand.surfaceStyle}>
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
          <Award className="h-12 w-12 text-[var(--market-on-surface-variant)]" />
          <h1 className="market-font-display text-xl font-bold text-[var(--market-on-surface)]">
            Marca no encontrada
          </h1>
          <Link
            to="/market/$slug"
            params={{ slug }}
            className="rounded-full bg-[var(--market-accent)] px-5 py-2.5 text-sm font-semibold text-[var(--primary-foreground)]"
          >
            Volver a la tienda
          </Link>
        </div>
      </div>
    );
  }

  return (
    <MarketLandingPage
      slug={slug}
      data={data}
      kind="brand"
      param={slugifyName(brandName)}
      brandName={brandName}
      title={brandName}
    />
  );
}
